import React, { useEffect, useState } from 'react';
import { Smartphone, X, Share2, PlusSquare } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * InstallPWAButton — invites the user to install WASABI CAPEX as a native-like
 * app on their device. Works on:
 *   • Android / Chrome / Edge → captures beforeinstallprompt and triggers prompt()
 *   • iOS Safari → opens a modal with manual "Add to Home Screen" instructions
 *
 * Renders NOTHING if:
 *   • The app is already running in standalone mode (already installed)
 *   • The browser doesn't support install AND it's not iOS
 *   • The user dismissed the prompt in this session (capex_install_dismissed)
 */
export const InstallPWAButton = ({ variant = 'sidebar' }) => {
  const { language } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [iosModalOpen, setIosModalOpen] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [dismissed, setDismissed] = useState(
    typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem('capex_install_dismissed') === '1'
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // 1) Detect "already installed" (standalone)
    const mql = window.matchMedia('(display-mode: standalone)');
    const checkStandalone = () =>
      setIsStandalone(mql.matches || window.navigator.standalone === true);
    checkStandalone();
    if (mql.addEventListener) mql.addEventListener('change', checkStandalone);

    // 2) Detect iOS (no beforeinstallprompt support — manual flow)
    const ua = window.navigator.userAgent || '';
    const iosLike = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(iosLike);

    // 3) Capture install prompt on supported browsers
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => {
      setDeferredPrompt(null);
      setIsStandalone(true);
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
      if (mql.removeEventListener) mql.removeEventListener('change', checkStandalone);
    };
  }, []);

  // Visibility logic
  if (isStandalone || dismissed) return null;
  // If neither a deferred prompt nor iOS, we can't install → don't show
  if (!deferredPrompt && !isIOS) return null;

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice?.outcome === 'dismissed') {
          sessionStorage.setItem('capex_install_dismissed', '1');
          setDismissed(true);
        }
        setDeferredPrompt(null);
      } catch (_) {
        /* ignore */
      }
    } else if (isIOS) {
      setIosModalOpen(true);
    }
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem('capex_install_dismissed', '1');
    setDismissed(true);
  };

  const labelMain = language === 'fr' ? 'Installer l\'app' : 'Install app';
  const labelHint = language === 'fr' ? 'Accès direct depuis votre écran' : 'Quick access from home screen';

  if (variant === 'sidebar') {
    return (
      <>
        <div
          className="mx-2 my-2 rounded-lg border border-orange-200 dark:border-orange-900/40 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/20 p-3 relative"
          data-testid="install-pwa-card"
        >
          <button
            onClick={handleDismiss}
            className="absolute top-1.5 right-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            title={language === 'fr' ? 'Masquer' : 'Dismiss'}
            data-testid="install-pwa-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <div className="flex items-start gap-2.5">
            <div className="flex-shrink-0 rounded-md bg-orange-500 p-1.5 text-white">
              <Smartphone className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-900 dark:text-slate-100">
                {labelMain}
              </p>
              <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-0.5 leading-tight">
                {labelHint}
              </p>
              <Button
                size="sm"
                onClick={handleInstall}
                className="mt-2 h-7 px-2 text-[11px] bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="install-pwa-button"
              >
                {language === 'fr' ? 'Installer' : 'Install'}
              </Button>
            </div>
          </div>
        </div>

        {/* iOS Manual Install Instructions */}
        <Dialog open={iosModalOpen} onOpenChange={setIosModalOpen}>
          <DialogContent className="sm:max-w-md" data-testid="ios-install-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-orange-500" />
                {language === 'fr'
                  ? 'Installer sur iPhone / iPad'
                  : 'Install on iPhone / iPad'}
              </DialogTitle>
              <DialogDescription>
                {language === 'fr'
                  ? 'Suivez ces 3 étapes dans Safari pour avoir un accès direct à WASABI CAPEX depuis votre écran d\'accueil.'
                  : 'Follow these 3 steps in Safari to access WASABI CAPEX directly from your home screen.'}
              </DialogDescription>
            </DialogHeader>
            <ol className="space-y-3 mt-2">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center">1</span>
                <div className="flex-1 text-sm">
                  {language === 'fr' ? (
                    <>Touchez l'icône <Share2 className="inline-block w-4 h-4 mx-1 text-blue-500" /> <strong>Partager</strong> en bas de l'écran.</>
                  ) : (
                    <>Tap the <Share2 className="inline-block w-4 h-4 mx-1 text-blue-500" /> <strong>Share</strong> icon at the bottom.</>
                  )}
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center">2</span>
                <div className="flex-1 text-sm">
                  {language === 'fr' ? (
                    <>Faites défiler et sélectionnez <PlusSquare className="inline-block w-4 h-4 mx-1 text-slate-600" /> <strong>« Sur l'écran d'accueil »</strong>.</>
                  ) : (
                    <>Scroll and choose <PlusSquare className="inline-block w-4 h-4 mx-1 text-slate-600" /> <strong>"Add to Home Screen"</strong>.</>
                  )}
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center">3</span>
                <div className="flex-1 text-sm">
                  {language === 'fr'
                    ? <>Touchez <strong>« Ajouter »</strong>. L'icône WASABI CAPEX apparaît sur votre écran.</>
                    : <>Tap <strong>"Add"</strong>. The WASABI CAPEX icon appears on your home screen.</>}
                </div>
              </li>
            </ol>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIosModalOpen(false)} data-testid="ios-install-close">
                {language === 'fr' ? 'Compris' : 'Got it'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return null;
};

export default InstallPWAButton;
